// tokenService.js - Token refresh and management
import { useAuthStore } from '../state';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

// Store the current refresh token promise to prevent multiple simultaneous requests
let refreshTokenPromise = null;

/**
 * Refreshes the access token using the HTTP-only refresh token cookie
 * @returns {Promise<string|null>} The new access token or null if refresh failed
 */
export const refreshAccessToken = async () => {
    // If a refresh is already in progress, return the existing promise
    if (refreshTokenPromise) {
        return refreshTokenPromise;
    }

    // Create a new refresh token promise
    refreshTokenPromise = (async () => {
        try {
            // The refresh token is now in an HTTP-only cookie
            // We don't need to send it in the request body
            const response = await fetch(`${apiBaseUrl}/refresh-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                // Include credentials to ensure cookies are sent
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`Refresh token request failed: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();

            // Update state with the new access token
            const expiryTime = Date.now() + (data.expiresIn * 1000);
            useAuthStore.getState().setAccessToken(data.accessToken);
            useAuthStore.getState().setTokenExpiry(expiryTime);

            // Setup the next token refresh
            setupTokenRefresh(Date.now() + (data.expiresIn * 1000));

            return data.accessToken;
        } catch (error) {
            console.error('Error refreshing token:', error);
            throw error;
        } finally {
            // Clear the promise reference once completed (whether successful or not)
            refreshTokenPromise = null;
        }
    })();

    return refreshTokenPromise;
};

/**
 * Setup token refresh timer
 * @param {number} expiryTime - Timestamp when the token expires
 */
export const setupTokenRefresh = (expiryTime) => {
    if (!expiryTime) return;

    const currentTime = Date.now();
    const timeUntilExpiry = expiryTime - currentTime;

    // If token is already expired, refresh immediately
    if (timeUntilExpiry <= 0) {
        refreshAccessToken();
        return;
    }

    // Refresh token 5 minutes before it expires
    const refreshTime = timeUntilExpiry - (5 * 60 * 1000);

    // Set timeout to refresh token
    setTimeout(() => {
        refreshAccessToken();
    }, refreshTime > 0 ? refreshTime : 0);
};
